import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ApiClientService, SessionStorageKey, SessionStorageService, ToastService } from '@services/index';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormGroup } from '@angular/forms';
import { FileUploadGroupForm } from '../../shared-agreement.model';
import { Permission } from 'app/enums';
import { RightPanelService } from '@services/right-panel.service';
import { debounceTime } from 'rxjs';
import { NgxExtendedPdfViewerComponent, NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { MetadataHighlightsService } from 'app/features/agreement/pages/metadata-highlights.service';
import { AgreementMetaDataConstants } from 'app/features/agreement/pages/agreement-metadata-generation.helper';
import { TextReference } from '../../../types/text-reference.type';
import { TranslateService } from '@ngx-translate/core';

// Re-export for backward compatibility
export type { TextReference };

@UntilDestroy()
@Component({
    selector: 'app-pdf-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrl: './pdf-viewer.component.scss'
})
export class PdfViewerComponent implements OnInit {
    @Input() public file: FormGroup<FileUploadGroupForm>;
    @Input() public id: string;
    @Output() public fileDialogStateChange = new EventEmitter<boolean>();
    @ViewChild(NgxExtendedPdfViewerComponent, { static: false }) private pdfViewer: NgxExtendedPdfViewerComponent;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly metadataHighlightsService = inject(MetadataHighlightsService);
    private readonly pdfViewerService = inject(NgxExtendedPdfViewerService);
    protected readonly translateService = inject(TranslateService);
    protected readonly toastService = inject(ToastService);

    public permissions = Permission;
    public uploading = true;
    public pdfByteArray: Uint8Array;
    private loaded = false;

    public isDragOver = false;
    public textReferences: TextReference[] = [];
    public currentHighlight: number = -1;

    public get f() {
        return this.file.controls;
    }

    public get disabled() {
        return this.f.file.disabled;
    }

    public ngOnInit() {
        console.log('init...');

        // Listen to panel expand/collapse events and trigger PDF zoom recalculation
        this.rightPanelService.isExpanded$
            .pipe(untilDestroyed(this), debounceTime(150)) // Wait for CSS transition to complete
            .subscribe(() => {
                this.updatePdfZoom();
            });

        this.rightPanelService.isCollapsed$.pipe(untilDestroyed(this), debounceTime(150)).subscribe(() => {
            this.updatePdfZoom();
        });

        // Subscribe to metadata highlights service to automatically highlight text when fields are focused
        this.metadataHighlightsService.highlightedFields$.pipe(untilDestroyed(this)).subscribe(() => {
            // Load all text references when highlights are set
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
            const textReferences = this.metadataHighlightsService.getAllTextReferences();
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (textReferences.size > 0) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                this.textReferences = Array.from(textReferences.values());
            }
        });

        // Listen to field focus events to trigger PDF highlighting
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.metadataHighlightsService.fieldFocused$.pipe(untilDestroyed(this)).subscribe((fieldType) => {
            console.log('PDF Viewer: Field focused event received:', fieldType);
            if (fieldType !== null) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.highlightFieldText(fieldType);
            }
        });

        if (this.f?.file?.value) {
            //console.log('id: ' + this.id + ', init form is set');
            if (this.isCached(this.f?.file?.value.url ?? this.f?.file?.value.id)) {
                //console.log('id: ' + this.id + ', init is cached');
                this.retrieveByteArrayIndexedDB('myByteArray')
                    .then((retrievedByteArray) => {
                        if (retrievedByteArray) {
                            this.pdfByteArray = retrievedByteArray;
                            this.uploading = false;
                            this.loaded = true;
                            this.changeDetectorRef.markForCheck();
                        }
                    })
                    .catch((error) => {
                        console.error('Error retrieving byte array:', error);
                    });
            } else if (this.f.file.value.url) {
                this.apiClientService.downloadTempFile(this.f.file.value.url).subscribe({
                    next: (response) => {
                        this.pdfByteArray = new Uint8Array(response);
                        this.cacheFile(this.f.file.value.url, this.pdfByteArray);
                        this.uploading = false;
                        this.changeDetectorRef.markForCheck();
                    },
                    error: (error) => {
                        console.error('Error downloading file:', error);
                        this.uploading = false;
                        this.changeDetectorRef.markForCheck();
                    }
                });
            } else if (this.f.file.value.id) {
                this.uploading = true;
                this.apiClientService.download(this.f.file.value.id).subscribe({
                    next: (response) => {
                        this.pdfByteArray = new Uint8Array(response);
                        this.cacheFile(this.f.file.value.id, this.pdfByteArray);
                        this.uploading = false;
                        this.changeDetectorRef.markForCheck();
                    },
                    error: (error) => {
                        console.error('Error downloading file:', error);
                        this.uploading = false;
                        this.f.file.setValue({ fileName: null, fileExtension: null, url: 'REMOVE' });
                        this.f.file.markAsDirty();
                        const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                        const message = this.translateService.instant(
                            'FEATURES.AGREEMENTS.TOAST_GET_PDF_ERROR.MESSAGE'
                        ) as string;
                        this.toastService.open(title, message, { type: 'error', progressBar: true });
                        this.changeDetectorRef.markForCheck();
                    }
                });
            }
        } else {
            this.uploading = false;
        }
    }

    public showViewer() {
        return this.loaded;
    }

    private updatePdfZoom() {
        // Trigger a window resize event which forces PDF.js to recalculate the zoom
        // This simulates what happens when the user manually changes zoom
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 400); // Wait for CSS transition to fully complete (300ms) + buffer
    }

    public resetFileUpload(event: Event) {
        this.fileDialogStateChange.emit(true);
        const element = event.currentTarget as HTMLInputElement;
        element.value = '';

        // Add focus listener to detect when dialog closes
        const onFocus = () => {
            setTimeout(() => {
                if (!element.files || element.files.length === 0) {
                    this.fileDialogStateChange.emit(false);
                }
            }, 100);
            window.removeEventListener('focus', onFocus);
        };
        window.addEventListener('focus', onFocus);
    }

    public uploadReport(event: Event) {
        this.uploading = true;
        const element = event.currentTarget as HTMLInputElement;
        if (element?.files && element.files.length > 0) {
            const file: File | null = element.files[0];
            this.handleFile(file);
        } else {
            // Dialog was canceled (no file selected)
            this.fileDialogStateChange.emit(false);
            this.uploading = false;
        }
    }

    public removeFile() {
        this.removeFileUpload();
        this.pdfByteArray = null;
        this.loaded = false;
        this.f.file.setValue({ fileName: null, fileExtension: null, url: 'REMOVE' });
        this.f.file.markAsDirty();
    }

    private removeFileUpload() {
        this.f.file.setValue(null);
        this.changeDetectorRef.markForCheck();
    }

    public onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    public onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    public onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            const file = event.dataTransfer.files[0];
            this.handleFile(file);
        }
    }

    private handleFile(file: File) {
        if (!file) return;
        this.uploading = true;
        this.removeFileUpload();

        const lastIndexOfDot = file.name.lastIndexOf('.');
        const fileExtension = file.name.slice(lastIndexOfDot + 1);

        this.apiClientService.upload(file).subscribe({
            next: (response) => {
                this.apiClientService.downloadTempFile(response).subscribe({
                    next: (resp) => {
                        this.pdfByteArray = new Uint8Array(resp);
                        this.f.file.setValue({
                            url: response,
                            fileName: file.name,
                            fileExtension: fileExtension
                        });
                        this.f.file.markAsDirty();
                        this.cacheFile(response, this.pdfByteArray);
                        this.uploading = false;
                        this.fileDialogStateChange.emit(false);
                        this.changeDetectorRef.markForCheck();
                    },
                    error: (error) => {
                        console.error('Error downloading temp file:', error);
                        this.uploading = false;
                        this.fileDialogStateChange.emit(false);
                        this.changeDetectorRef.markForCheck();
                    }
                });
            },
            error: (error) => {
                console.error('Error uploading file:', error);
                this.uploading = false;
                this.fileDialogStateChange.emit(false);
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    private isCached(id: string) {
        const fileId = this.sessionStorageService.getItem(SessionStorageKey.CachedPdfFile);
        return id == fileId;
    }

    private cacheFile(id: string, byteArray: Uint8Array) {
        this.sessionStorageService.setItem(SessionStorageKey.CachedPdfFile, id);
        this.storeByteArrayIndexedDB('myByteArray', byteArray);
        this.loaded = true;
        console.log('id: ' + this.id + ', stored');
    }

    private storeByteArrayIndexedDB(key: string, byteArray: Uint8Array) {
        const request: IDBOpenDBRequest = indexedDB.open('byteArrayDB', 1);

        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
            const db = (event.target as IDBOpenDBRequest).result;
            db.createObjectStore('byteArrays', { keyPath: 'key' });
        };

        request.onsuccess = (event: Event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            const transaction = db.transaction(['byteArrays'], 'readwrite');
            const store = transaction.objectStore('byteArrays');
            store.put({ key, byteArray });
        };

        request.onerror = (event: Event) => {
            console.error('IndexedDB error:', event);
        };
    }

    private async retrieveByteArrayIndexedDB(key: string): Promise<Uint8Array | null> {
        return new Promise((resolve, reject) => {
            const request: IDBOpenDBRequest = indexedDB.open('byteArrayDB', 1);

            request.onsuccess = (event: Event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                const transaction = db.transaction(['byteArrays'], 'readonly');
                const store = transaction.objectStore('byteArrays');
                const getRequest: IDBRequest = store.get(key);

                getRequest.onsuccess = (event: Event) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const result = (event.target as IDBRequest).result;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if (result?.byteArray) {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                        resolve(result.byteArray);
                    } else {
                        resolve(null); // No data found
                    }
                };

                getRequest.onerror = (event: Event) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(event);
                };
            };

            request.onerror = (event: Event) => {
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject(event);
            };
        });
    }

    /**
     * Highlight text in PDF based on field metadata (called by field focus events)
     * @param fieldType The field enum value that was focused
     */
    public highlightFieldText(fieldType: AgreementMetaDataConstants) {
        const fieldName = AgreementMetaDataConstants[fieldType];
        console.log('highlightFieldText called for:', fieldName, 'enum:', fieldType);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const textReference = this.metadataHighlightsService.getFieldTextReference(fieldName);
        console.log('Text reference found:', textReference);
        console.log('All text references:', this.textReferences);

        if (!textReference) {
            console.warn('No text reference found for field:', fieldName);
            return;
        }

        // Find the index of this reference
        const referenceIndex = this.textReferences.findIndex((ref) => ref.fieldName === fieldName);
        console.log('Reference index:', referenceIndex);

        if (referenceIndex >= 0) {
            this.highlightReference(referenceIndex);
        } else {
            console.warn('Reference not found in textReferences array');
        }
    }

    /**
     * Set text references from AI metadata
     * @param references Array of text references with search text and optional page numbers
     */
    public setTextReferences(references: TextReference[]) {
        this.textReferences = references;
        this.currentHighlight = -1;
    }

    /**
     * Highlight text in the PDF by searching for it
     * @param referenceIndex Index of the reference to highlight
     */
    public highlightReference(referenceIndex: number) {
        if (referenceIndex < 0 || referenceIndex >= this.textReferences.length) {
            console.warn('Invalid reference index:', referenceIndex);
            return;
        }

        const reference = this.textReferences[referenceIndex];
        this.currentHighlight = referenceIndex;

        // Use the PDF viewer's search functionality to find and highlight text
        this.searchAndHighlightText(reference.text, reference.pageNumber);
    }

    /**
     * Search and highlight text in the PDF
     * @param searchText Text to search for
     * @param pageNumber Optional page number to navigate to
     */
    private searchAndHighlightText(searchText: string, pageNumber?: number) {
        console.log('searchAndHighlightText called with text:', searchText, 'page:', pageNumber);
        if (!this.pdfViewer) {
            console.warn('PDF Viewer is not initialized');
            return;
        }

        // Navigate to specific page if provided
        if (pageNumber && pageNumber > 0) {
            this.pdfViewer.page = pageNumber;
        }

        // Wait for the PDF to be fully loaded before searching
        setTimeout(() => {
            console.log('Calling pdfViewerService.find() for:', searchText);
            try {
                // Clear previous search first
                void this.pdfViewerService.find('');

                // Then search for the new text
                setTimeout(() => {
                    void this.pdfViewerService.find(searchText, {
                        highlightAll: true,
                        matchCase: false
                    });
                    console.log('Search command executed successfully');
                }, 100);
            } catch (error) {
                console.error('Error during PDF search:', error);
            }
        }, 300);
    }

    /**
     * Clear all highlights
     */
    public clearHighlights() {
        this.currentHighlight = -1;
        void this.pdfViewerService.find('');
    }
}
